import { ChangeCandidateDto } from 'src/app/data-transfer/entities/change-candidate-dto';
import { ImportTypeSetting, PackagingImportHelper } from './packaging-import-helper';
import { Observable } from 'rxjs';
import { ImportCandidateDto } from 'src/app/data-transfer/entities/import-candidate-dto';
import { ImportExportApiService } from 'src/app/data-transfer/services/import-export-api-service';
import { ComponentNavigationService } from 'src/app/navigation/services/navigation-services/component-navigation-service';
import { ComponentTypeService } from 'src/app/navigation/services/component-type-service';
import { ChangeComponentCandidateDto } from 'src/app/data-transfer/entities/component-entities/change-component-candidate-dto';
import { ImportComponentCandidateDto } from 'src/app/data-transfer/entities/component-entities/import-component-candidate-dto';
import { PackagingComponentTypesEnum } from 'src/app/model/packaging-component-types-enum';

export class PackagingComponentImportHelper implements PackagingImportHelper {
  constructor(
    private importExportApiService: ImportExportApiService,
    private componentNavigationService: ComponentNavigationService,
    private componentTypeService: ComponentTypeService
  ) {}

  getTableTitle(importType: ImportTypeSetting, archiveActive: boolean): string {
    if (importType === ImportTypeSetting.Import) {
      if(archiveActive) {
        return 'dataManagement.import.import.tableTitleComponentArchive';
      }
      return 'dataManagement.import.import.tableTitleComponent';
    }
    return 'dataManagement.import.changes.tableTitleComponent';
  }

  getDisplayedColumns(): string[] {
    return [
      'itemId',
      'version',
      'exportingOrganization',
      'packagingComponentCategoryName',
      'packagingComponentSubtypeName',
      'articleName',
      'articleNumber',
      'timestamp',
      'action',
      'select',
    ];
  }

  getPackagingImportCandidates(): Observable<ImportCandidateDto[]> {
    return this.importExportApiService.getComponentImportCandidates();
  }

  getPackagingChangeCandidates(): Observable<ChangeCandidateDto[]> {
    return this.importExportApiService.getComponentTrackedChanges();
  }

  getDeleteFunction(importType: ImportTypeSetting): (candidate: number) => Observable<any> {
    if (importType === ImportTypeSetting.Import) {
      return this.getDeleteFunctionImportCandidate();
    }
    return this.getDeleteFunctionChangeCandidate();
  }

  getImportFunctionImportCandidate(): (candidateId: number, targetDirectoryId: number, isWithTracking: boolean) => Observable<any> {
    return (candidateId: number, targetDirectoryId: number, isWithTracking: boolean) =>
      this.importExportApiService.importComponents(candidateId, targetDirectoryId, isWithTracking);
  }

  getChangeFunctionChangeCandidate(): (changeId: number) => Observable<any> {
    return (changeId: number) => this.importExportApiService.applyComponentChanges(changeId);
  }

  getPackagingIdsFromChangeCandidates(selectedChanges: ChangeCandidateDto[]): number[] {
    return selectedChanges.map(x => (x as ChangeComponentCandidateDto).existingPackagingComponent.id ?? -1).filter(x => x !== -1);
  }

  navigateToCandidate(candidate: ImportCandidateDto): void {
    const componentCandidate = candidate as ImportComponentCandidateDto;
    const componentTypeName = this.componentTypeService.getComponentTypeNameById(componentCandidate.packagingComponent.packagingComponentTypeId);
    if (componentCandidate.packagingComponent.packagingComponentCategoryId === PackagingComponentTypesEnum.Decoration) {
      this.componentNavigationService.navigateToDecorationPreview(componentTypeName, candidate.id);
    } else {
      this.componentNavigationService.navigateToComponentPreview(componentTypeName, candidate.id);
    }
  }

  navigateToChange(candidate: ChangeCandidateDto, seePackagingBeforeChange: boolean): void {
    const change = candidate as ChangeComponentCandidateDto;
    const component = seePackagingBeforeChange ? change.existingPackagingComponent : change.updatedPackagingComponent;
    const id = seePackagingBeforeChange ? change.existingPackagingComponent.id : change.importCandidateId;
    if (id == null) {
      return;
    }
    const componentTypeName = this.componentTypeService.getComponentTypeNameById(component.packagingComponentTypeId);
    if (component.packagingComponentCategoryId === PackagingComponentTypesEnum.Decoration) {
      this.componentNavigationService.navigateToDecorationPreview(componentTypeName, id, true, seePackagingBeforeChange);
    } else {
      this.componentNavigationService.navigateToComponentPreview(componentTypeName, id, true, seePackagingBeforeChange);
    }
  }

  private getDeleteFunctionImportCandidate(): (candidate: number) => Observable<any> {
    return (candidateId: number) => this.importExportApiService.deleteComponentImportCandidate(candidateId);
  }

  private getDeleteFunctionChangeCandidate(): (candidate: number) => Observable<any> {
    return (changeId: number) => this.importExportApiService.ignoreComponentChanges(changeId);
  }
}
